/**
 * Formats a decimal time value to a MM:SS format
 * @param {number} time - The time in decimal minutes
 * @returns {string} - The formatted time string
 */
export const formatTime = (time) => {
  if (!time && time !== 0) return '--:--';
  
  // Convert to number if it's a string
  const timeValue = typeof time === 'string' ? parseFloat(time) : time;
  
  // Handle NaN values
  if (isNaN(timeValue)) return '--:--';
  
  const minutes = Math.floor(timeValue);
  const seconds = Math.round((timeValue - minutes) * 60);
  
  // Handle case where seconds rounds up to 60
  if (seconds === 60) {
    return `${minutes + 1}:00`;
  }
  
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Formats a decimal hours value to a "X hr Y min" format
 * @param {number} timeInHours - The time in decimal hours
 * @returns {string} - The formatted time string
 */
export const formatHoursMinutes = (timeInHours) => {
  if (!timeInHours && timeInHours !== 0) return '--';
  
  const hours = Math.floor(timeInHours);
  const minutes = Math.round((timeInHours - hours) * 60);
  
  if (hours === 0) {
    return `${minutes} min`;
  } else if (minutes === 0) {
    return `${hours} hr`;
  } else {
    return `${hours} hr ${minutes} min`;
  }
};

/**
 * Formats a distance value with appropriate units
 * @param {number} distance - The distance value
 * @param {string} unit - The unit of the distance ('miles' or 'kilometers')
 * @returns {string} - The formatted distance string
 */
export const formatDistance = (distance, unit) => {
  if (!distance && distance !== 0) return '--';
  
  // Check if distance is a number before using toFixed
  if (typeof distance !== 'number') {
    console.warn('formatDistance called with non-number value:', distance);
    return '--';
  }
  
  const formattedDistance = distance.toFixed(1);
  const unitAbbr = unit === 'kilometers' ? 'km' : 'mi';
  
  return `${formattedDistance} ${unitAbbr}`;
};

/**
 * Format a pace value (e.g. 10.5) as a readable string (e.g. "10:30 min/mi")
 * 
 * @param {number} pace - The pace value in decimal form (e.g. 10.5)
 * @param {string} unit - The unit of distance (e.g. "mi" or "km")
 * @returns {string} - Formatted pace string
 */
export const formatPace = (pace, unit = 'mi') => {
  if (pace === null || pace === undefined || isNaN(pace)) {
    return '—';
  }
  
  const minutes = Math.floor(pace);
  const seconds = Math.round((pace - minutes) * 60);
  
  return `${minutes}:${seconds.toString().padStart(2, '0')} min/${unit}`;
};

/**
 * Format a time in hours to a readable string (e.g. "10:30")
 * 
 * @param {number} timeInHours - Time in decimal hours (e.g. 10.5)
 * @returns {string} - Formatted time string
 */
export const formatTimeHours = (timeInHours) => {
  if (timeInHours === null || timeInHours === undefined || isNaN(timeInHours)) {
    return '—';
  }
  
  const hours = Math.floor(timeInHours);
  const minutes = Math.round((timeInHours - hours) * 60);
  
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Convert a 12-hour clock time (hours 1-12, AM/PM) to a decimal 24-hour value.
 *
 * @param {number|string} hours12 - Hours on a 12-hour clock (1-12). Values outside that
 *   range are accepted and reduced into a sensible 24-hour result.
 * @param {number|string} minutes - Minutes (0-59).
 * @param {string} ampm - 'AM' or 'PM' (case-insensitive). Defaults to 'AM'.
 * @returns {number} - Decimal hours in the 24-hour clock (e.g. 13.5 for 1:30 PM).
 */
export const to24Hour = (hours12, minutes, ampm) => {
  const h = parseInt(hours12, 10);
  const m = parseInt(minutes, 10);
  const safeH = Number.isFinite(h) ? h : 0;
  const safeM = Number.isFinite(m) ? m : 0;
  const isPM = String(ampm || 'AM').toUpperCase() === 'PM';
  // 12 AM = 0, 12 PM = 12; otherwise add 12 for PM.
  let hour24 = safeH % 12;
  if (isPM) hour24 += 12;
  return hour24 + safeM / 60;
};

/**
 * Given an aid station's saved cutoff time-of-day and the race start time, return the
 * elapsed hours from race start to that cutoff. If `prevElapsedHours` is supplied, the
 * result is forced monotonically non-decreasing by adding 24-hour days as needed so
 * cutoffs that wrap past midnight (multi-day races) are handled correctly.
 *
 * @param {object} station - Aid station object with cutoffHours/cutoffMinutes/cutoffAMPM.
 * @param {object} startInfo - { startTimeHours, startTimeMinutes, startTimeAMPM }.
 * @param {number} [prevElapsedHours=0] - Elapsed hours at the previous cutoff (for
 *   monotonicity across multiple cutoffs).
 * @returns {number|null} - Elapsed hours from race start, or null if no cutoff is set.
 */
export const computeCutoffElapsedHours = (station, startInfo, prevElapsedHours = 0) => {
  if (!station || !station.hasCutoffTime) return null;
  const hoursSet = station.cutoffHours !== '' && station.cutoffHours != null;
  const minutesSet = station.cutoffMinutes !== '' && station.cutoffMinutes != null;
  if (!hoursSet && !minutesSet) return null;

  const cutDecimal = to24Hour(
    station.cutoffHours || 0,
    station.cutoffMinutes || 0,
    station.cutoffAMPM || 'AM'
  );
  const startDecimal = to24Hour(
    startInfo?.startTimeHours ?? 0,
    startInfo?.startTimeMinutes ?? 0,
    startInfo?.startTimeAMPM ?? 'AM'
  );

  let elapsed = cutDecimal - startDecimal;
  // Roll forward by full days until we land at or after the previous cutoff (or 0).
  const floor = Number.isFinite(prevElapsedHours) ? prevElapsedHours : 0;
  while (elapsed < floor) elapsed += 24;
  return elapsed;
};

/**
 * Format decimal hours as a compact "Xh Ym" string (e.g. 5.75 -> "5h 45m").
 *
 * @param {number} hours - Decimal hours.
 * @returns {string}
 */
export const formatElapsedShort = (hours) => {
  if (hours === null || hours === undefined || Number.isNaN(hours)) return '';
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

/**
 * Format a saved aid-station cutoff as a 12-hour clock string (e.g. "11:45 AM").
 * Returns '' if the station has no cutoff set.
 */
export const formatCutoffTimeOfDay = (station) => {
  if (!station || !station.hasCutoffTime) return '';
  const hoursSet = station.cutoffHours !== '' && station.cutoffHours != null;
  const minutesSet = station.cutoffMinutes !== '' && station.cutoffMinutes != null;
  if (!hoursSet && !minutesSet) return '';
  const h = parseInt(station.cutoffHours || '0', 10) || 0;
  const m = parseInt(station.cutoffMinutes || '0', 10) || 0;
  const ampm = station.cutoffAMPM || 'AM';
  // Display 0 as 12 for a friendlier clock (shouldn't happen normally since the
  // picker is 1-12, but guard anyway).
  const displayH = h === 0 ? 12 : h;
  return `${displayH}:${String(m).padStart(2, '0')} ${ampm}`;
};

/**
 * Build an array of elapsed hours from race start for each aid station index
 * (null where that station has no cutoff). Handles multi-day monotonicity.
 */
export const buildCutoffElapsedByIndex = (aidStations = [], startInfo) => {
  const result = [];
  let prev = 0;
  aidStations.forEach((station) => {
    const elapsed = computeCutoffElapsedHours(station, startInfo, prev);
    result.push(elapsed);
    if (elapsed != null) prev = elapsed;
  });
  return result;
};

/**
 * Required average running pace (decimal min/unit) from the previous cutoff (or start)
 * to this aid station's cutoff clock time.
 *
 * @param {object} station - Aid station (matched to segment by location).
 * @param {object[]} aidStations - Full aid station list (for elapsed index lookup).
 * @param {Array<number|null>} cutoffElapsedByIndex - Parallel to aidStations array order.
 * @param {number} [endDistanceFallback] - Segment total distance if station.location is missing.
 */
export const computeLegCutoffPaceMinutes = (
  station,
  aidStations,
  cutoffElapsedByIndex,
  endDistanceFallback
) => {
  if (!station?.hasCutoffTime) return null;

  const stationIndex = (aidStations || []).findIndex((s) => s === station);
  const idx =
    stationIndex >= 0
      ? stationIndex
      : (aidStations || []).findIndex((s) => {
          const a = parseFloat(s.location);
          const b = parseFloat(station.location);
          return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < 0.06;
        });
  if (idx < 0) return null;

  const elapsedEnd = cutoffElapsedByIndex?.[idx];
  if (elapsedEnd == null || !Number.isFinite(elapsedEnd)) return null;

  let locEnd = parseFloat(station.location);
  if (!Number.isFinite(locEnd) || locEnd <= 0) {
    locEnd = endDistanceFallback;
  }
  if (!Number.isFinite(locEnd)) return null;

  let elapsedStart = 0;
  let locStart = 0;
  for (let i = idx - 1; i >= 0; i -= 1) {
    const prev = aidStations[i];
    if (prev?.hasCutoffTime && cutoffElapsedByIndex[i] != null) {
      elapsedStart = cutoffElapsedByIndex[i];
      locStart = parseFloat(prev.location) || 0;
      break;
    }
  }

  const legElapsed = elapsedEnd - elapsedStart;
  const legDist = locEnd - locStart;
  if (legDist <= 0.01 || legElapsed <= 0) return null;
  return (legElapsed * 60) / legDist;
};

/**
 * Required pace for the finish leg using overall race cutoff time and distance.
 */
export const computeFinishLegCutoffPaceMinutes = (formData, cutoffElapsedByIndex) => {
  const cutoffTime = parseFloat(formData?.cutoffTime);
  const distance = parseFloat(formData?.distance);
  if (!Number.isFinite(cutoffTime) || !Number.isFinite(distance) || distance <= 0) {
    return null;
  }

  const stations = formData?.aidStations || [];
  let elapsedStart = 0;
  let locStart = 0;
  for (let i = stations.length - 1; i >= 0; i -= 1) {
    if (stations[i]?.hasCutoffTime && cutoffElapsedByIndex[i] != null) {
      elapsedStart = cutoffElapsedByIndex[i];
      locStart = parseFloat(stations[i].location) || 0;
      break;
    }
  }

  const legElapsed = cutoffTime - elapsedStart;
  const legDist = distance - locStart;
  if (legDist <= 0.01 || legElapsed <= 0) return null;
  return (legElapsed * 60) / legDist;
};

/**
 * Average pace from race start to this aid's cutoff (elapsed ÷ distance from mile 0).
 */
export const computeCutoffPaceFromStartMinutes = (
  station,
  cutoffElapsedByIndex,
  stationIdx,
  endDistanceFallback
) => {
  if (!station?.hasCutoffTime) return null;
  if (stationIdx < 0) return null;
  const elapsed = cutoffElapsedByIndex?.[stationIdx];
  if (elapsed == null || !Number.isFinite(elapsed) || elapsed <= 0) return null;

  let dist = parseFloat(station.location);
  if (!Number.isFinite(dist) || dist <= 0) {
    dist = endDistanceFallback;
  }
  if (!Number.isFinite(dist) || dist <= 0) return null;
  return (elapsed * 60) / dist;
};

/** Pace from start to finish using overall race cutoff time. */
export const computeFinishCutoffPaceFromStartMinutes = (formData) => {
  const cutoffTime = parseFloat(formData?.cutoffTime);
  const distance = parseFloat(formData?.distance);
  if (!Number.isFinite(cutoffTime) || !Number.isFinite(distance) || distance <= 0) {
    return null;
  }
  return (cutoffTime * 60) / distance;
};

/** Format leg cutoff pace for display, or "-" when unavailable. */
export const formatLegCutoffPace = (paceMinutes, unitAbbr = 'mi') => {
  if (paceMinutes == null || !Number.isFinite(paceMinutes)) return '-';
  return formatPace(paceMinutes, unitAbbr);
};

/**
 * Format a file size in bytes to a human-readable string (e.g. "1.23 MB")
 * 
 * @param {number} bytes - Size in bytes
 * @param {number} decimals - Number of decimal places to include
 * @returns {string} - Formatted file size string
 */
export const formatFileSize = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
}; 